/**
 * The library's data seams.  Plain `fetch` in a hook, the same shape
 * `useAtlas` uses — this app has no query client and one page is not a
 * reason to introduce one.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { toQueryString, type LibraryQuery } from "./filters";
import type {
  ReplacePlan,
  ReplaceResult,
  ReviewReading,
  ShelfPayload,
  ShelvesPayload,
  UnitDetailPayload,
  UnitsPayload,
  Verdict,
} from "./types";

const BASE = "/api/coaching/library";

/**
 * A unit ref, safe to carry in a URL PATH.
 *
 * `#` is the catalog-unit separator and also the fragment separator, so
 * a verbatim `zai:x/y@3#c2` would be cut at the hash and the request
 * would ask for the whole vessel instead — which does not fail, it
 * quietly answers about the wrong thing.  `:`, `@` and `/` are left
 * alone so the path the server logs reads like the ref a reviewer
 * pasted; this is exactly the backend's own `quote(ref, safe=":@/")`.
 */
function refPath(ref: string): string {
  return encodeURIComponent(ref)
    .replace(/%2F/g, "/")
    .replace(/%3A/g, ":")
    .replace(/%40/g, "@");
}

/**
 * Why a request failed, in the two flavours the page renders
 * differently.
 *
 * `forbidden` is a signed-in reader who is not staff, and it is NOT an
 * error to retry or sign in for — there is nothing behind it for them.
 * `unauthorized` is a signed-out one, who has somewhere to go.  Folding
 * both into "request failed" would show the second reader a dead end
 * and the first a sign-in link they have already used.
 */
export type Failure =
  | { kind: "unauthorized" }
  | { kind: "forbidden" }
  | { kind: "error"; detail: string };

export type Loaded<T> =
  | { status: "loading"; data: T | null; failure: null }
  | { status: "ready"; data: T; failure: null }
  | { status: "failed"; data: null; failure: Failure };

async function get<T>(url: string, signal: AbortSignal): Promise<T> {
  const response = await fetch(url, { credentials: "include", signal });
  if (response.status === 401) throw { kind: "unauthorized" } satisfies Failure;
  if (response.status === 403) throw { kind: "forbidden" } satisfies Failure;
  if (!response.ok) {
    throw { kind: "error", detail: `request failed (${response.status})` } satisfies Failure;
  }
  return (await response.json()) as T;
}

function asFailure(error: unknown): Failure {
  if (error && typeof error === "object" && "kind" in error) return error as Failure;
  return { kind: "error", detail: error instanceof Error ? error.message : "could not reach the library" };
}

/**
 * A fetch that keeps the last good data visible while the next one is
 * in flight.
 *
 * Blanking the shelf on every chip click is what makes a filter bar
 * feel broken — the reader clicks "level 2" and the list they were
 * reading disappears before the shorter one arrives.  Holding the
 * previous rows and dimming them is the difference between a page that
 * responds and one that reloads.
 */
function useEndpoint<T>(url: string | null): Loaded<T> & { reload: () => void } {
  const [state, setState] = useState<Loaded<T>>({ status: "loading", data: null, failure: null });
  const [nonce, setNonce] = useState(0);
  const held = useRef<T | null>(null);

  useEffect(() => {
    if (url === null) return undefined;
    const controller = new AbortController();
    setState({ status: "loading", data: held.current, failure: null });
    get<T>(url, controller.signal)
      .then((data) => {
        held.current = data;
        setState({ status: "ready", data, failure: null });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setState({ status: "failed", data: null, failure: asFailure(error) });
      });
    return () => controller.abort();
  }, [url, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, reload };
}

/** The ring → planet → claim tree, with review state and totals.
 *
 * Narrowed by the pipeline view for the same reason the unit shelf is:
 * a map that badged a planet with units the reader has filtered away
 * would send them clicking into an empty shelf.
 */
export function useShelves(pipeline = "") {
  const suffix = pipeline ? `?pipeline=${encodeURIComponent(pipeline)}` : "";
  return useEndpoint<ShelvesPayload>(`${BASE}/shelves${suffix}`);
}

/**
 * The filtered shelf of catalog units, and its facet counts.
 *
 * `limit` rides the WIRE rather than slicing what came back: a shelf
 * larger than one page would otherwise let "show more" run out of rows
 * to reveal while the count above it still promised more, which is the
 * kind of dead end that reads as a broken page rather than a full one.
 */
export function useUnits(query: LibraryQuery, limit: number) {
  const search = toQueryString(query);
  const suffix = `${search ? `${search}&` : ""}limit=${limit}`;
  return useEndpoint<UnitsPayload>(`${BASE}/units?${suffix}`);
}

/**
 * One unit in full.  `null` closes the panel without a request.
 *
 * Either spelling of the ref is accepted by the endpoint.  The page
 * hands it the ADDRESS when it has one, because that pins the version
 * the row on screen actually is — a name would resolve to the latest,
 * which is the same answer until somebody republishes underneath the
 * reader.
 */
export function useUnitDetail(unitRef: string | null) {
  return useEndpoint<UnitDetailPayload>(unitRef ? `${BASE}/units/${refPath(unitRef)}` : null);
}

function csrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : "";
}

export type Sent<T> = { ok: true; data: T } | { ok: false; detail: string };

/**
 * POST a body and resolve to the failure rather than throwing.
 *
 * Every caller has to RENDER the failure: a review that silently did
 * not save, or a republish that silently did not run, is worse than one
 * that visibly did not — the operator walks away believing the database
 * changed.
 */
async function post<T>(path: string, body: unknown): Promise<Sent<T>> {
  try {
    const response = await fetch(`${BASE}${path}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken() },
      body: JSON.stringify(body),
    });
    if (response.ok) return { ok: true, data: (await response.json()) as T };
    const detail = await response
      .json()
      .then((payload) => (payload as { detail?: string }).detail)
      .catch(() => undefined);
    return { ok: false, detail: detail ?? `failed (${response.status})` };
  } catch (error) {
    return { ok: false, detail: error instanceof Error ? error.message : "the request did not go" };
  }
}

/**
 * Record a verdict on one catalog unit.
 *
 * The ref rides the BODY, not the path: a unit ref carries slashes and a
 * `#`, and a greedy path converter would swallow a trailing `/review`
 * into it.  The note is filed under the PERMANENT name whichever
 * spelling is sent, which is why the page sends the permanent one — a
 * verdict is recorded against what is published now, and a version pin
 * would only be dropped server-side anyway.
 */
export function recordNote(unitRef: string, verdict: Verdict, note: string) {
  return post<{ permanentRef: string; review: ReviewReading }>("/review", {
    unitRef,
    verdict,
    note,
  });
}

/** The selection's current feedback, as plain text. */
export function exportNotes(unitRefs: string[]) {
  return post<{ text: string; units: number }>("/export", { unitRefs });
}

/** What replacing these files would do — reads only. */
export function planReplacement(filenames: string[]) {
  return post<ReplacePlan>("/republish/plan", { filenames });
}

/** Purge and republish.  `clearLearnerState` is explicit consent. */
export function republish(filenames: string[], clearLearnerState = false) {
  return post<ReplaceResult>("/republish", { filenames, clearLearnerState });
}

/** Every publishable file on the fixture shelves, by ring and planet. */
export function useShelf(open: boolean) {
  return useEndpoint<ShelfPayload>(open ? `${BASE}/shelf` : null);
}

/**
 * True while the viewport is too narrow for three columns.
 *
 * Layout, not content: the same shelves, the same rows, stacked.  A
 * reviewer opening a flagged planet on a phone should see the same
 * library, and a page that only worked at desk width would simply not
 * be used for the half of reviewing that happens elsewhere.
 */
export function useStacked(breakpoint = 900): boolean {
  const [stacked, setStacked] = useState(() =>
    typeof window === "undefined" ? false : window.innerWidth <= breakpoint,
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const query = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const onChange = () => setStacked(query.matches);
    onChange();
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, [breakpoint]);
  return stacked;
}
